import { memo, useState, type TouchEvent, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle, 
  Plane, 
  Building2, 
  Stethoscope, 
  HeartHandshake,
  Clock,
  Phone,
  FileText,
  Car,
  Utensils,
  ChevronLeft,
  ChevronRight,
  Package
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { IncludedItem } from "@/types/schema";

const defaultIncludedItems: IncludedItem[] = [
  {
    icon: Plane,
    title: "Airport Transfers",
    description: "Complimentary pickup and drop-off at international airports",
    category: "travel",
  },
  {
    icon: Building2,
    title: "Hospital Stay",
    description: "Private/semi-private room during your treatment period",
    category: "medical",
  },
  {
    icon: Stethoscope,
    title: "Surgeon & Anesthesia",
    description: "All surgical fees, anesthesia, and operating room charges",
    category: "medical",
  },
  {
    icon: HeartHandshake,
    title: "Post-Op Care",
    description: "Nursing care, medications, and dressings during hospital stay",
    category: "medical",
  },
  {
    icon: Phone,
    title: "24/7 Coordinator",
    description: "Dedicated patient coordinator available round the clock",
    category: "support",
  },
  {
    icon: Clock,
    title: "Follow-Up Visits",
    description: "Post-discharge consultations and check-ups included",
    category: "medical",
  },
  {
    icon: FileText,
    title: "Medical Reports",
    description: "Complete documentation for your home physician",
    category: "support",
  },
  {
    icon: Car,
    title: "Local Transport",
    description: "Hospital and hotel transfers during your stay",
    category: "travel",
  },
  {
    icon: Utensils,
    title: "Hospital Meals",
    description: "Nutritious meals tailored to your dietary requirements",
    category: "medical",
  },
];

interface WhatsIncludedSectionProps {
  items?: IncludedItem[];
}

export const WhatsIncludedSection = memo(function WhatsIncludedSection({ items }: WhatsIncludedSectionProps) {
  const [activeCategory, setActiveCategory] = useState("all");
  const [currentSlide, setCurrentSlide] = useState(0);

  // Use provided items or fallback to default
  const includedItems = items && items.length > 0 ? items : defaultIncludedItems;

  const categories = [
    { id: "all", label: "All Included", count: includedItems.length },
    { id: "medical", label: "Medical", count: includedItems.filter(i => i.category === "medical").length },
    { id: "travel", label: "Travel", count: includedItems.filter(i => i.category === "travel").length },
    { id: "support", label: "Support", count: includedItems.filter(i => i.category === "support").length },
  ];

  // Reset slide when items change
  useEffect(() => {
    setCurrentSlide(0);
    setActiveCategory("all");
  }, [items]);

  // Swipe handlers
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  const minSwipeDistance = 50;
  
  const onTouchStart = (e: TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e: TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && totalSlides > 1) {
      nextSlide();
    }
    if (isRightSwipe && totalSlides > 1) {
      prevSlide();
    }
  };

  const filteredItems = activeCategory === "all" 
    ? includedItems 
    : includedItems.filter(item => item.category === activeCategory);

  const itemsPerSlide = 3;
  const totalSlides = Math.ceil(filteredItems.length / itemsPerSlide);
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const visibleItems = filteredItems.slice(
    currentSlide * itemsPerSlide,
    (currentSlide + 1) * itemsPerSlide
  );

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3 sm:pb-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent/10 flex-shrink-0">
              <Package className="w-5 h-5 text-accent" />
            </div>
            <div className="min-w-0">
              <CardTitle as="h2" className="text-lg sm:text-xl">What's Included</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Comprehensive care package
              </p>
            </div>
          </div>

          {/* Category Filter - Scrollable on mobile */}
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide max-w-full">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setCurrentSlide(0);
                }}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex-shrink-0",
                  activeCategory === cat.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}
              >
                {cat.label}
                <span className="ml-1 opacity-70">({cat.count})</span>
              </button>
            ))}
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-3 sm:px-6 pt-0">
        {/* Items Grid */}
        <div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 touch-pan-y"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {visibleItems.map((item, index) => (
            <div 
              key={`${item.title}-${index}`}
              className="p-3 sm:p-4 rounded-xl border border-border/50 hover:border-primary/30 hover:shadow-soft transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-accent/10 group-hover:bg-accent/20 transition-colors shrink-0">
                  <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium text-secondary text-sm mb-1 truncate">{item.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        {totalSlides > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-border/50">
            <div className="flex gap-2">
              {Array.from({ length: totalSlides }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all",
                    currentSlide === i ? "bg-primary w-6" : "bg-muted hover:bg-muted-foreground/30"
                  )}
                />
              ))}
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8 rounded-full"
                onClick={prevSlide}
                disabled={totalSlides <= 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8 rounded-full"
                onClick={nextSlide}
                disabled={totalSlides <= 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Trust Badge */}
        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <CheckCircle className="w-4 h-4 text-accent" />
          <span>All-inclusive pricing — No hidden fees or surprise charges</span>
        </div>
      </CardContent>
    </Card>
  );
});
